import { CommonModule, NgIf, NgSwitchCase } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { KENDO_BUTTON, KENDO_BUTTONGROUP, KENDO_CHIPLIST, KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons'
import { DatePickerComponent } from '@progress/kendo-angular-dateinputs'
import { KENDO_WINDOW, WindowRef, WindowService } from '@progress/kendo-angular-dialog'
import { ErrorComponent, FormFieldComponent, KENDO_CHECKBOX, MaskedTextBoxComponent, NumericTextBoxComponent, TextBoxDirective } from '@progress/kendo-angular-inputs'
import { LabelComponent } from '@progress/kendo-angular-label'
import {
  distinctUntilChanged,
  EMPTY,
  forkJoin,
  mergeMap,
  startWith,
  Subscription,
  tap,
} from 'rxjs'

import { DropDownListComponent, KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns'
import { ToastrService } from 'ngx-toastr'
import * as CryptoJS from 'crypto-js'
import { environment } from '../../../../environments/environment'
import { ClientServices } from '../../../@theme/services/clients.service'
import { TypesServices } from '../../../@theme/services/typeslist.service'
import { UserStateService } from '../../../@theme/services/user-state.service'
import { AuthRepDashComponent } from '../../../components/authDash/authrepdash.component'
import {
  Assistant,
  BdState,
  IaState,
  Lookups,
  Note,
  OutsideActivity,
  PersonalAccount,
  Rep,
  RepCode,
  RepFirm,
  RepPro,
  RepSocialMedia,
  RepSubscription,
  Solicitor,
} from '../../../models/repEditorModels'
import { UserInfo } from '../../../models/userModels'
import { RepEditorStateService } from '../shared/rep-editor-state.service'
import { RepEditorAssistantComponent } from './rep-editor-assistant/rep-editor-assistant.component'
import { RepEditorBdStatesComponent } from './rep-editor-bd-states/rep-editor-bd-states.component'
import { RepEditorIaStatesComponent } from './rep-editor-ia-states/rep-editor-ia-states.component'
import { RepEditorLicenseComponent } from './rep-editor-license/rep-editor-license.component'
import { RepEditorLogComponent } from './rep-editor-log/rep-editor-log.component'
import { RepEditorOutsideActivityComponent } from './rep-editor-outside-activity/rep-editor-outside-activity.component'
import { RepEditorPersonalAccountsComponent } from './rep-editor-personal-accounts/rep-editor-personal-accounts.component'
import { RepEditorProfessionalComponent } from './rep-editor-professional/rep-editor-professional.component'
import { RepEditorRegistrationComponent } from './rep-editor-registration/rep-editor-registration.component'
import { RepEditorRepCodesComponent } from './rep-editor-rep-codes/rep-editor-rep-codes.component'
import { RepEditorSocialMediaComponent } from './rep-editor-social-media/rep-editor-social-media.component'
import { RepEditorSolicitorComponent } from './rep-editor-solicitor/rep-editor-solicitor.component'
import { RepEditorSubscriptionComponent } from './rep-editor-subscription/rep-editor-subscription.component'
import { AdminOnlyDirective } from './shared/admin-only.directive'
import { PHONE_MASK, toPhoneDigits } from '../../../@theme/pipes/phone.pipe'

export enum RepSectionEnum {
  ComonModule,
  FormsModule,
  ReactiveFormsModule,
  Details,
  Assistant,
  BdStates,
  IaStates,
  License,
  Log,
  PersonalAccounts,
  OutsideActivity,
  Professional,
  Registration,
  RepCodes,
  SocialMedia,
  Solicitor,
  Subscription,
  AdminOnlyDirective
}

export enum ActionEnum {
  Add,
  Edit,
}

@Component({
  selector: 'gd-rep-editor',
  templateUrl: './rep-editor.component.html',
  styleUrls: ['./rep-editor.component.scss'],
  imports: [
    CommonModule,
    KENDO_BUTTON,
    KENDO_BUTTONGROUP,
    NgSwitchCase,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    TextBoxDirective,
    ErrorComponent,
    DatePickerComponent,
    MaskedTextBoxComponent,
    KENDO_DROPDOWNBUTTON,
    KENDO_DROPDOWNLIST,
    DropDownListComponent,
    KENDO_CHIPLIST,
    KENDO_CHECKBOX,
    KENDO_WINDOW,
    LabelComponent,
    NumericTextBoxComponent,
    RepEditorAssistantComponent,
    RepEditorIaStatesComponent,
    RepEditorBdStatesComponent,
    RepEditorLicenseComponent,
    RepEditorLogComponent,
    RepEditorPersonalAccountsComponent,
    RepEditorOutsideActivityComponent,
    RepEditorProfessionalComponent,
    RepEditorRegistrationComponent,
    RepEditorRepCodesComponent,
    RepEditorSocialMediaComponent,
    RepEditorSolicitorComponent,
    RepEditorSubscriptionComponent,
/*     AdminOnlyDirective, */
  ]
})
export class RepEditorComponent implements OnInit, OnDestroy {
  missingFields: string[] = [];
  public editRep: Rep
  public editorForm: FormGroup
  public lookups: Lookups = new Lookups()
  public currentRepId: number
  public defaultDateOfBirth: Date = new Date('1/5/2000')
  public calculatedAge: string
  private getReprold = ''
  // State
  public ssnMask = '000-00-0000'
  public phoneMask = PHONE_MASK
  public defaultState = {
    stateId: null,
    stateText: 'Choose a State',
    stateValue: null,
  }
  public defaultCrdBranchNumber = 'Select a Branch'
  public repSectionEnum = RepSectionEnum
  public currentSection = RepSectionEnum.Details
  public action = ActionEnum.Add
  public actionEnum = ActionEnum
  public sameAsWorkAddress: FormControl = new FormControl(false)
  private sameAsWorkSubscription: Subscription
  public windowRef: WindowRef = null
  
  public get isAdmin(): boolean {
    const storageValue = localStorage.getItem('rvarietylist')
    if (!storageValue) {
      return false
    }
    try {
      const decryptedRole = CryptoJS.AES.decrypt(
        storageValue,
        environment.securepw,
      ).toString(CryptoJS.enc.Utf8)
      return decryptedRole?.toLowerCase().includes('admin')
    } catch (error) {
      console.error('Error checking admin role:', error)
      return false
    }
  }
  
  constructor(
    private formBuilder: FormBuilder,
    private clientServices: ClientServices,
    private route: ActivatedRoute,
    private repState: RepEditorStateService,
    private typesService: TypesServices,
    private router: Router,
    private userState: UserStateService,
    private toastrService: ToastrService,
    private windowService: WindowService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.clientServices.getRepLookups().subscribe((response) => {
        this.lookups = response
        if (params['repId']) {
          this.currentRepId = Number(params['repId'])
          if (this.currentRepId !== this.repState?.repId) {
            this.initialize()
          }
        } else {
          this.editRep = new Rep(null)
         // console.log(JSON.stringify(this)
          this.editRep.active = true
        }
      })
    })
    this.editorForm = this.formBuilder.group({
      repId: 0,
      firstName: ['', Validators.required],
      middleName: null,
      lastName: ['', Validators.required],
      shortName: null,
      title: null,
      dob: null,
      ssn: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      webAddress: null,
      insRepName: null,
      hireDate: null,
      terminationDate: null,
      crdnumber: null,
      branchNumber: ['', Validators.required],
      brokerRep: null,
      bankRep: null,
      accessPerson: null,
      doNotPrint: null,
      notProducer: null,
      receiveAlerts: null,
      receiveApprovals: null,
      workAddress1: null,
      workAddress2: null,
      workCity: null,
      workState: null,
      workZip: null,
      mailAddress1: null,
      mailAddress2: null,
      mailCity: null,
      mailState: null,
      mailZip: null,
      homeAddress1: null,
      homeAddress2: null,
      homeCity: null,
      homeState: null,
      homeZip: null,
      workPhone: null,
      workExt: null,
      homePhone: null,
      mobilePhone: null,
      fax: null,
      adminFee: null,
      nasdannualFee: null,
      nasdbranchFee: null,
      active: null

    });
  }

  ngOnInit() {
    Object.keys(this.editorForm.controls).forEach((field) => {
      this.editorForm.controls[field].markAsTouched();
    });
    this.applyReadOnly()
    this.sameAsWorkSubscription = this.sameAsWorkAddress.valueChanges
      .pipe(
        distinctUntilChanged(),
        mergeMap((sameAddress) => {
          if (sameAddress) {
            const address1 = this.editorForm
              .get('workAddress1')
              .valueChanges.pipe(
                startWith(this.editorForm.get('workAddress1').value),
                tap((value) =>
                  this.editorForm.get('mailAddress1').setValue(value),
                ),
              )
            const address2 = this.editorForm
              .get('workAddress2')
              .valueChanges.pipe(
                startWith(this.editorForm.get('workAddress2').value),
                tap((value) =>
                  this.editorForm.get('mailAddress2').setValue(value),
                ),
              )
            const city = this.editorForm.get('workCity').valueChanges.pipe(
              startWith(this.editorForm.get('workCity').value),
              tap((value) => this.editorForm.get('mailCity').setValue(value)),
            )
            const state = this.editorForm.get('workState').valueChanges.pipe(
              startWith(this.editorForm.get('workState').value),
              tap((value) => this.editorForm.get('mailState').setValue(value)),
            )
            const zip = this.editorForm.get('workZip').valueChanges.pipe(
              startWith(this.editorForm.get('workZip').value),
              tap((value) => this.editorForm.get('mailZip').setValue(value)),
            )
            return forkJoin([address1, address2, city, state, zip])
          } else {
            this.editorForm.get('mailAddress1').reset()
            this.editorForm.get('mailAddress2').reset()
            this.editorForm.get('mailCity').reset()
            this.editorForm.get('mailState').reset()
            this.editorForm.get('mailZip').reset()
            return EMPTY
          }
        }),
      )
      .subscribe()
  }

  ngOnDestroy() {
    this.repState.clearRepState()
    this.sameAsWorkSubscription.unsubscribe()
  }

  // Only Admin can edit rep fields; everyone else gets a read-only view
  private applyReadOnly(): void {
    if (!this.isAdmin) {
      this.editorForm.disable()
      this.sameAsWorkAddress.disable()
    }
  }

  public initialize() {
    this.clientServices
      .getRepById({ repId: this.currentRepId })
      .subscribe((response) => {
        this.editRep = new Rep(response)
        this.editorForm = this.buildForm(this.editRep)
        this.applyReadOnly()
        this.action = ActionEnum.Edit
        this.initializeState()
      })
  }

  public initializeState() {
    this.repState.clearRepState()
    this.repState.repId = this.currentRepId
    this.clientServices
      .getAssistantForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repAssistants = response.map(
          (assistant) => new Assistant(assistant),
        )
        this.repState.repAssistantsActive = this.repState.repAssistants.filter(
          (a) => a.active,
        )
      })
    this.clientServices
      .getBdStatesForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repBdStates = response.map((rbs) => new BdState(rbs))
      })
    this.clientServices
      .getIaStatesForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repIaStates = response.map((ris) => new IaState(ris))
      })
    this.clientServices
      .getRepLicenseForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repLicenses = response
      })
    this.clientServices
      .getNotesForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repNotes = response.map((note) => new Note(note))
      })
    this.clientServices
      .getOutsideActivityForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repOutsideActivities = response.map(
          (oa) => new OutsideActivity(oa),
        )
      })
    this.clientServices
      .getPersonalAccountsForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repPersonalAccounts = response.map(
          (pa) => new PersonalAccount(pa),
        )
      })
    this.clientServices
      .getRepProForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repPros = response.map((rp) => new RepPro(rp))
      })
    this.clientServices
      .getRepFirmForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repFirms = response.map((rf) => new RepFirm(rf))
      })
    this.clientServices
      .getRepCodesForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repCodes = response.map((rc) => new RepCode(rc))
      })
    this.clientServices
      .getRepSocialMediaForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repSocialMedia = response.map(
          (rsm) => new RepSocialMedia(rsm),
        )
      })
    this.clientServices
      .getSolicitorForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repSolicitors = response.map((s) => new Solicitor(s))
      })
    this.clientServices
      .getRepSubscriptionForRep({ repId: this.repState.repId })
      .subscribe((response) => {
        this.repState.repSubscriptions = response.map(
          (rs) => new RepSubscription(rs),
        )
      })
    this.typesService.getAssistantLookups().subscribe((response) => {
      this.repState.assistantLookups = response
    })
    this.typesService.getActiveTypeStatelist().subscribe((response) => {
      this.repState.stateList = response
    })
    this.typesService.getLicenseList().subscribe((response) => {
      this.repState.licenseList = response
    })
    this.typesService.getNoteTypes().subscribe((response) => {
      this.repState.noteTypeList = response
    })
    this.typesService.getProTypes().subscribe((response) => {
      this.repState.proTypes = response
    })
    this.typesService.getFirmTypes().subscribe((response) => {
      this.repState.firmTypeList = response
    })
    this.typesService.getApprovedSocialMedia().subscribe((response) => {
      this.repState.approvedSocialMedia = response
    })
    this.typesService.getSubscription().subscribe((response) => {
      this.repState.subscriptions = response
    })
  }

  onSubmit(event) {
    event.preventDefault();
    this.missingFields = [];
    Object.keys(this.editorForm.controls).forEach(key => {
      if (this.editorForm.controls[key].invalid) {
        this.missingFields.push(key);
      }
    });


    const repToSubmit = new Rep(this.editorForm.value)
    this.clientServices.submitRepUpdate(repToSubmit).subscribe((response) => {
      this.router.navigate(['/pages/rep-editor/edit'], {
        queryParams: { repId: response },
      })
      this.toastrService.success('Rep form has been updated', 'Success')
    })
  }

  goToViewReps() {
    this.router.navigate(['/pages/view-reps/default'])
  }

  goToMembership(event: MouseEvent): void {
    event.stopPropagation();
    if (this.windowRef) {
      this.windowRef.close()
    }
    const passrepemail = { Email: this.editRep.email }
    if (this.editRep.email !== null) {
      this.clientServices.getRepRole(passrepemail).subscribe((response) => {
        if (response !== undefined) {
          this.getReprold = response
        }
        this.userState.currentUserInfo = new UserInfo({
          email: this.editRep.email,
          firstName: this.editRep.firstName,
          lastName: this.editRep.lastName,
          phoneNumber: this.editRep.mobilePhone,
          roles: [this.getReprold],
        })
      })
      this.userState.currentUserInfo = new UserInfo({
        email: this.editRep.email,
        firstName: this.editRep.firstName,
        lastName: this.editRep.lastName,
        phoneNumber: this.editRep.mobilePhone,
        roles: [this.getReprold],
      })
      const windowRef = this.windowService.open({
        title: 'Web Portal',
        content: AuthRepDashComponent,
        width: 800,
        height: 675,
      });

      // You MUST cast content.instance to your component type to pass parameter and value
      const componentInstance = windowRef.content.instance as AuthRepDashComponent;
      componentInstance.branchlist = this.editorForm.get('branchNumber')?.value

    }
  }


  public switchSection(section: RepSectionEnum) {
    this.currentSection = section
  }

  public buildForm(rep: Rep): UntypedFormGroup {
    return this.formBuilder.group({
      repId: rep.repId,
      firstName: [rep.firstName, Validators.required],
      middleName: rep.middleName,
      lastName: [rep.lastName, Validators.required],
      shortName: rep.shortName,
      title: rep.title,
      dob: rep.dob,
      ssn: [rep.ssn, Validators.required],
      email: [rep.email, [Validators.required, Validators.email]],
      webAddress: rep.webAddress,
      insRepName: rep.insRepName,
      hireDate: rep.hireDate,
      terminationDate: rep.terminationDate,
      crdnumber: rep.crdnumber,
      branchNumber: [rep.branchNumber, Validators.required],
      brokerRep: rep.brokerRep,
      bankRep: rep.bankRep,
      accessPerson: rep.accessPerson,
      doNotPrint: rep.doNotPrint,
      notProducer: rep.notProducer,
      receiveAlerts: rep.receiveAlerts,
      receiveApprovals: rep.receiveApprovals,
      workAddress1: rep.workAddress1,
      workAddress2: rep.workAddress2,
      workCity: rep.workCity,
      workState: rep.workState,
      workZip: rep.workZip,
      mailAddress1: rep.mailAddress1,
      mailAddress2: rep.mailAddress2,
      mailCity: rep.mailCity,
      mailState: rep.mailState,
      mailZip: rep.mailZip,
      homeAddress1: rep.homeAddress1,
      homeAddress2: rep.homeAddress2,
      homeCity: rep.homeCity,
      homeState: rep.homeState,
      homeZip: rep.homeZip,
      workPhone: toPhoneDigits(rep.workPhone),
      workExt: rep.workExt,
      homePhone: toPhoneDigits(rep.homePhone),
      mobilePhone: toPhoneDigits(rep.mobilePhone),
      fax: toPhoneDigits(rep.fax),
      adminFee: rep.adminFee,
      nasdannualFee: rep.nasdannualFee,
      nasdbranchFee: rep.nasdbranchFee,
      active: rep.active,
    })
  }

  public itemDisabled(itemArgs: { dataItem: any; index: number }) {
    return itemArgs.index === -1
  }

  calculatePrimaryAge(date: Date) {
    const millisecDifference = Date.now() - date.getTime()
    const millisecPerDay = 24 * 60 * 60 * 1000
    const millisecPerYear = 365.25 * millisecPerDay

    const yearDifference = Math.floor(millisecDifference / millisecPerYear)
    this.calculatedAge = yearDifference + 'yo'
  }
}
